import { createSelector } from "@reduxjs/toolkit"
import PropTypes from "prop-types"
import { useMemo } from "react"
import { useSelector } from "react-redux"

import Button from "components/ui/button"
import Image from "components/ui/image"
import { imageLookup } from "helpers/product/image-lookup"
import { useNavigation } from "helpers/use-navigation"
import { cn } from "styles/utils"

const makeSelectPrefData = (pref) => {
  const selectWizardData = (state) => state.wizard.data[pref]

  return createSelector(
    [selectWizardData, (state) => state.choices],
    (wizardData, choices) => {
      const selectedValue = choices[pref]?.value
      const selectedOption = (wizardData?.options || []).find(
        (opt) => opt.value === selectedValue
      )

      const thumbnailImage = selectedOption?.imageKey
        ? imageLookup(pref, selectedOption.imageKey)
        : selectedOption?.image

      return {
        stoneName: selectedOption?.stoneName || selectedOption?.title,
        thumbnailImage,
        wizardHeadingTitle: wizardData?.heading?.title,
      }
    }
  )
}

const GuidedStoneSelectorThumbnail = ({
  currentPref,
  handleGoToPref,
  index,
  pref,
}) => {
  const keyboardProps = useNavigation({ direction: "both" })
  const selectPrefData = useMemo(() => makeSelectPrefData(pref), [pref])
  const { stoneName, thumbnailImage, wizardHeadingTitle } =
    useSelector(selectPrefData)
  const isOpened = useSelector((state) =>
    state.panel.openedPanelIndexes.includes("stone-selector")
  )

  const label = thumbnailImage ? `${wizardHeadingTitle}: ${stoneName}` : null
  const isActive = isOpened && currentPref === pref

  return (
    <Button
      aria-controls="wizard-accordion-content-stone-selector"
      className={cn(
        "border-grey-300 size-44 items-center justify-center overflow-hidden rounded-full border -outline-offset-1 hover:opacity-90 focus:opacity-90",
        isActive && "border-primary bg-primary/10"
      )}
      data-cy={`product-wizard-heading-${pref}`}
      disabled={!thumbnailImage}
      onClick={() => handleGoToPref(pref)}
      tabIndex={(isOpened ? currentPref === pref : index === 0) ? 0 : -1}
      {...(label
        ? {
            "aria-label": `Edit ${label}`,
            title: label,
          }
        : { "aria-hidden": true })}
      {...(isActive && { "aria-current": "step" })}
      {...keyboardProps}
    >
      {thumbnailImage && (
        <Image
          className="size-36"
          height="40"
          lazyload={false}
          width="40"
          {...(thumbnailImage.x2
            ? {
                src: thumbnailImage.x2,
                srcSet: `${thumbnailImage.x1} 1x, ${thumbnailImage.x2} 2x`,
              }
            : { src: thumbnailImage })}
        />
      )}
    </Button>
  )
}

GuidedStoneSelectorThumbnail.propTypes = {
  currentPref: PropTypes.string,
  handleGoToPref: PropTypes.func,
  index: PropTypes.number,
  pref: PropTypes.string,
}

export default GuidedStoneSelectorThumbnail
